import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, FolderOpen, Building2, Sparkles } from 'lucide-react';
import { getBalance } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useActiveProject } from '../context/ActiveProjectContext';
import Drawer from '../components/Drawer';
import StatCards from '../components/StatCards';
import NewProjectFormScreen from '../components/NewProjectFormScreen';
import CurrencyPicker from '../components/CurrencyPicker';
import PermissionNoticeCard from '../components/PermissionNoticeCard';

const ProjectOverviewPage = () => {
  const {
    user,
    activeOrg,
    canViewDashboardCards,
    canViewProjects,
    canViewTransactions,
    canCreateProjects,
    canCreateTransactions,
    canEditTransactions,
    canDeleteTransactions,
    canExportTransactions,
    canViewProjectHistory,
  } = useAuth();

  const {
    selectedProject,
    selectedProjectId,
    openProjectPicker,
    loading: loadingActiveProject,
    refreshProjects,
    createAndSelectProject,
  } = useActiveProject();

  const [balance, setBalance] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [projectFormOpen, setProjectFormOpen] = useState(false);

  const organizationName =
    activeOrg?.name ||
    user?.organization_name ||
    user?.org_name ||
    user?.organization?.name ||
    user?.company_name ||
    'Organization';

  const hasDashboardAccess = Boolean(
    canViewDashboardCards ||
      canViewProjects ||
      canViewTransactions ||
      canCreateProjects ||
      canCreateTransactions ||
      canEditTransactions ||
      canDeleteTransactions ||
      canExportTransactions ||
      canViewProjectHistory
  );

  const fetchBalance = useCallback(async () => {
    if (!selectedProjectId) {
      setBalance({
        current_balance: 0,
        total_income: 0,
        total_expense: 0,
      });
      return;
    }

    try {
      const res = await getBalance({ project_id: selectedProjectId });

      setBalance(
        res?.data?.data || {
          current_balance: 0,
          total_income: 0,
          total_expense: 0,
        }
      );
    } catch (err) {
      console.error(err);

      setBalance({
        current_balance: 0,
        total_income: 0,
        total_expense: 0,
      });
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance, refreshKey]);

  useEffect(() => {
    if (!loadingActiveProject && !selectedProjectId) {
      openProjectPicker();
    }
  }, [loadingActiveProject, selectedProjectId, openProjectPicker]);

  const handleBalanceRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const pageContent = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {!hasDashboardAccess ? (
        <div className="mb-5">
          <PermissionNoticeCard
            title="Dashboard Access Restricted"
            message="You currently do not have permission from your Organization Head or Organization Admin to access this dashboard module."
            hint="Your layout, profile, and any other permitted areas remain available."
          />
        </div>
      ) : null}

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-3">
          <div>
            <h1 className="text-xl font-black text-slate-900">
              Welcome back,{' '}
              <span style={{ color: '#c44569' }}>{user?.name}</span>
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">
              Manage projects, balances, and organization insights from one place.
            </p>
          </div>

          <div className="inline-flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-sm bg-white"
               style={{
                 borderColor: '#f4d6df',
                 background:
                   'linear-gradient(135deg, #fff 0%, #fff7fb 45%, #fef2f8 100%)',
               }}>
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl"
              style={{
                background: 'linear-gradient(135deg, #c44569 0%, #e76f8a 100%)',
                boxShadow: '0 10px 24px rgba(196, 69, 105, 0.22)',
              }}
            >
              <Building2 size={18} color="#fff" />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">
                  Organization
                </span>
                <Sparkles size={13} color="#c44569" />
              </div>
             <div className="mt-1 text-base font-semibold text-slate-800 break-words">
  {organizationName}
</div>
            </div>
          </div>

          <p className="text-sm text-slate-400 mt-0.5">
            {selectedProject ? (
              <>
                Active project:{' '}
                <span className="font-semibold text-slate-600">
                  {selectedProject.name}
                </span>
              </>
            ) : (
              'Select a project to get started'
            )}
          </p>
        </div>

        {hasDashboardAccess ? (
          <div className="flex items-center gap-2 flex-wrap">
            <CurrencyPicker />
            {canCreateProjects ? (
              <button
                type="button"
                onClick={() => setProjectFormOpen(true)}
                className="px-3 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
                style={{ background: '#0f766e' }}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                New Project
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {hasDashboardAccess ? (
        <div className="mb-5">
          {selectedProject ? (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border"
              style={{
                borderColor: '#16a34a',
                background: '#f0fdf4',
                color: '#15803d',
              }}
            >
              <CheckCircle2 size={15} color="#16a34a" />
              {selectedProject.name}
              {selectedProject.description ? (
                <span className="font-normal text-slate-500 hidden sm:inline">
                  — {selectedProject.description}
                </span>
              ) : null}
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border cursor-pointer"
              style={{
                borderColor: '#f9b8ca',
                background: '#fff6f8',
                color: '#c44569',
              }}
              onClick={openProjectPicker}
            >
              <FolderOpen size={15} color="#c44569" />
              No project selected — click here to choose
            </div>
          )}
        </div>
      ) : null}

      {hasDashboardAccess ? <StatCards balance={balance} /> : null}

      <NewProjectFormScreen
        isOpen={projectFormOpen}
        onClose={() => setProjectFormOpen(false)}
        onSubmit={async (payload) => {
          await createAndSelectProject(payload);
          setProjectFormOpen(false);
          await refreshProjects();
          handleBalanceRefresh();
        }}
      />
    </div>
  );

  return <Drawer pageHeader={pageContent} />;
};

export default ProjectOverviewPage;